<template>
  <div>
    <w-data-table
      class="w-attachment-table"
      v-bind="filterProps($props, 'TRUNCATE_OPTIONS', 'headers', 'files')"
      :files="internalItems"
      :headers="internalHeaders"
      :truncate-options="internalTruncateOptions"
      :items-per-page="files.length"
      :hide-readonly-headers="[]"
      :has-max-height="false"
    >
      <template 
        #[`item.image`]="{ item }"
      >
        <div>
          <v-img
            :src="handleThumbnail(item)"
            :aspect-ratio="1"
            :class="getThumbnailClass(item)"
            @click="handlePreviewItem(item)"
          />
        </div>
      </template>
      <template 
        v-for="(index, name) in $scopedSlots" 
        #[name]="data"
      >
        <slot 
          :name="name" 
          v-bind="data" 
        />
      </template>
      <template #[`item.actions`]="{ item }">
        <w-table-action-wrapper class="d-flex">
          <div class="order-1">
            <w-export-button
              v-if="linkToOrigin && hasOrigin(item)"
              :icon="getIcon(item)"
              @click="navigateToOrigin(item)"
            >
              <template #tooltip>
                {{ $t('Projects.words.navigateToOrigin') }}
              </template>
            </w-export-button>
          </div>
          <div class="order-2">
            <w-icon-button
              v-if="!disableEdit && !readonly && item.id && workClientConnected == true"
              :icon="getIcon(item)"
              @click="handleOpenFile(item)"
            >
              <template #tooltip>
                {{ $t('Components.sentences.viewAndEditFile') }}
              </template>
            </w-icon-button>
          </div>
          <div class="order-3">
            <w-view-button
              v-if="isPreviewable(item) && !disableRead"
              @click="handlePreviewItem(item)"
            >
              <template #tooltip>
                {{ $t('Components.sentences.openImagePreview') }}
              </template>
            </w-view-button>
          </div>
          <div class="order-4">
            <w-edit-button
              v-if="!readonly && showEditButton"
              @click="editFile(item)"
            >
              <template #tooltip>
                {{ $t('Components.sentences.renameFile') }}
              </template>
            </w-edit-button>
          </div>
          <div class="order-5">
            <w-download-button
              v-if="!disableRead"
              @click="handleDownloadFile(item)"
            >
              <template #tooltip>
                {{ $t('Common.download') }}
              </template>
            </w-download-button>
          </div>
          <div class="order-6">
            <w-delete-button
              v-if="!disableRemove && !readonly"
              @click="handleRemoveFile(item)"
            >
              <template #tooltip>
                {{ $t('Common.delete') }}
              </template>
            </w-delete-button>
          </div>
        </w-table-action-wrapper>
      </template>
    </w-data-table>

    <ImagePreviewDialog 
      ref="imagePreviewDialog" 
      :disable-edit="disableEdit" 
    />
    <ImagePreviewDialog 
      ref="imagePreviewDialog" 
      :disable-edit="disableEdit" 
    />
    <DefaultDeleteDialog
      :ref="DELETE_DIALOG_REF"
      :message="deleteWarningText"
      :delete-label="$t('Common.delete')"
    />
    <FileRenameDialog
      ref="editNameDialog"
      @updateItem="updateItem"
    />
  </div>
</template>

<script>
// components
import WorkDataTable from '@/components/base_components/tables/WorkDataTable';
// helpers
import tableService from '@/components/base_components/tables/services/table-service';
import dateFormatter from '@/services/formatter_services/date-formatter';
import filesUtils from '@/utils/files-utils';
import filesService from '@/services/files-service';
import { invokeGetFileFromServer } from '@/services/invoke-get-file-service';
import { filterProps } from '@/utils/functional-component-utils';
import i18n from '@/language/i18n';
import cloneDeep from 'lodash/cloneDeep';
import { isImage, isPreviewable, handlePreviewItem } from '@/components/global_components/tables/composables/file-preview-service';

const DEFAULT_HEADERS = Object.freeze([
  {
    text: i18n.t('Common.file'),
    value: 'data',
    presentWith: filesUtils.formatFilename,
    truncateAfter: 50,
  },
  {
    text: i18n.t('Common.changed'),
    value: 'data.updatedAt',
    sortable: false,
    presentWith: dateFormatter.formatDateToLocaleDateString,
  },
  {
    text: i18n.t('Common.actions'),
    value: 'actions',
    align: 'right',
    sortable: false,
  },
]);

const TRUNCATE_OPTIONS = (headers) =>
  tableService.generateTruncateOptions(headers);

const _byName = (pathName) => (file) => file.data?.pathName != pathName;
const DELETE_DIALOG_REF = 'deleteFileDialog';

export default {
  name: 'WorkFilesTable',
  props: {
    ...WorkDataTable.props,
    headers: { type: Array, default: () => DEFAULT_HEADERS },
    truncateOptions: { type: Object },
    files: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
    workClientConnected: { type: Boolean, default: false },
    disableRemove: { type: Boolean, default: false },
    disableEdit: { type: Boolean, default: false },
    disableRead: { type: Boolean, default: false },
    showDeleteWarning: { type: Boolean, default: false },
    deleteWarningText: {
      type: String,
      default: i18n.t('Components.sentences.deleteFiles'),
    },
    previewableItem: { type: Boolean, default: false },
    editableFilename: { type: Boolean, default: false },
    showEditButton: { type: Boolean, default: true },
  linkToOrigin: { type: Boolean, default: false },

  },
  components: {
    ImagePreviewDialog: () =>
      import('@/components/global_components/dialogs/ImagePreviewDialog'),
    DefaultDeleteDialog: () =>
      import('@/components/global_components/dialogs/DefaultDeleteDialog'),
    FileRenameDialog: () =>
      import('@/components/global_components/dialogs/FileRenameDialog')
  },
  data() {
    return {
      DELETE_DIALOG_REF: DELETE_DIALOG_REF,
      DEFAULT_HEADERS: DEFAULT_HEADERS,
    };
  },
  computed: {
    internalTruncateOptions() {
      return TRUNCATE_OPTIONS(this.headers);
    },
    internalHeaders() {
      if (this.headers)
        return this.headers;
      
      return DEFAULT_HEADERS;
    },
    internalItems() {
      return cloneDeep(this.items);
    },
  },
  methods: {
    isImage,
    isPreviewable,
    handlePreviewItem,
    invokeGetFileFromServer,
    updateItem(item) {
      let id = item?.id ?? item?.uuid;
      let value = item?.id ? 'id' : 'uuid';
      const indexOfItem = this.internalItems.findIndex(f => f[value] == id);
      this.internalItems[indexOfItem].data = item?.data;      
      this.$emit('updateFiles', this.internalItems);
    },
    editFile(item){
      this.$refs.editNameDialog.open(item);
    },
    filterProps: filterProps,
    async handleDownloadFile(file) {
      try {
        const response = await this.invokeGetFileFromServer(file);
        filesUtils.downloadFile(
          response.data,
          file.data.originalName
        );
      } catch (e) {
        throw e;
      }
    },
    getThumbnailClass(item) {
      if (isImage(item)) {
        return 'thumbnail-image';
      }
      return 'thumbnail-placeholder';
    },
    handleRemoveFile(item) {
      if (this.showDeleteWarning) {
        this.$refs[DELETE_DIALOG_REF].open().then(() =>
          this.emitDeleteItems(item)
        );
      } else this.emitDeleteItems(item);
    },
    emitDeleteItems(item) {
      this.$emit('input', this.files.filter(_byName(item?.data?.pathName)));
    },
    handleOpenFile(item) {
      try {
        // opens file on work client
        filesService.openFile(item.id);
      } catch (e) {
        throw e;
      }
    },
    hasOrigin(item){
      return !!(item?.data?.activityId || item?.data?.deviationId);
    },
    navigateToOrigin(item){
      const activityId = item?.data?.activityId;
      if(activityId){
        try {
          const activityRoute = this.$router.resolve({ name: 'ActivityForm', params: { id: activityId }});
          if(activityRoute?.href){
            window.open(activityRoute.href, '_blank');
            return;
          }
        } catch (e) { console.warn('Failed to resolve ActivityForm route', e); }
      }
      const deviationId = item?.data?.deviationId;
      if(deviationId){
        try {
          const deviationRoute = this.$router.resolve({ name: 'ProcessDeviationForm', params: { id: deviationId }});
          if(deviationRoute?.href){
            window.open(deviationRoute.href, '_blank');
            return;
          }
        } catch (e) { console.warn('Failed to resolve DeviationForm route', e); }
      }
    },
    handleThumbnail(item) {
      if (isImage(item)) {
        return item.data.thumbnail;
      } else {
        let format = item.data.mimeType;
        if (filesUtils.isWordType(format)) {
          return require('@/assets/icons/thumbnail_placeholder/thumbnail_word_placeholder.svg');
        }
        if (filesUtils.isExcelType(format)) {
          return require('@/assets/icons/thumbnail_placeholder/thumbnail_excel_placeholder.svg');
        }
        if (item.data.mimeType == 'application/pdf') {
          return require('@/assets/icons/thumbnail_placeholder/thumbnail_pdf_placeholder.svg');
        } else
          return require('@/assets/icons/thumbnail_placeholder/thumbnail_document_placeholder.svg');
      }
    },
    getIcon(item) {
      if (item.data.mimeType === 'application/pdf') {
        return '$wPdf';
      } else if (item.data.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        return '$wWord';
      } else if (this.linkToOrigin) {
        return '$wExport'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.w-attachment-table {
  flex: 1;
  overflow: auto;
}
.thumbnail-image {
  width: 70px;
}
.thumbnail-placeholder {
  width: 20px;
  height: 24px;
  margin-left: 25px;
}
.filename {
  align-items: center;
}
</style>